/**
 * Promotions — the flyer view. Active campaign codes (quarterly promos etc.)
 * with their date windows and items at promo price. Tapping an item resolves
 * it to the inventory detail screen; "Add" drops it straight into the cart at
 * the promo price as a Spire-priced (non-overridden) line.
 */

import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, type PromoGroup, type PromoItem } from '../lib/api.js';
import { money2 } from '../lib/format.js';
import { toast } from '../lib/toast-store.js';
import { QueryError } from '../components/query-error.js';

export function PromotionsScreen() {
  const promos = useQuery({
    queryKey: ['promotions'],
    queryFn: ({ signal }) => api.promotions.list(signal),
    staleTime: 5 * 60_000,
  });

  return (
    <>
      <div className="topbar">
        <div>
          <h2>Promotions</h2>
          <div className="sub">
            {promos.data ? `${promos.data.length} active promo${promos.data.length === 1 ? '' : 's'}` : 'Current campaigns'}
          </div>
        </div>
        <Link to="/" className="back-btn">‹ Home</Link>
      </div>

      <div style={{ paddingTop: 8 }}>
        {promos.isPending && Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="kd-skeleton" style={{ height: 90 }} />
        ))}
        {promos.isError && <QueryError message={promos.error.message} onRetry={() => void promos.refetch()} />}
        {promos.data?.length === 0 && (
          <div className="kd-muted" style={{ padding: 18 }}>No active promotions right now.</div>
        )}
        {promos.data?.map((p) => <PromoCard key={p.code} promo={p} />)}
      </div>
    </>
  );
}

function PromoCard({ promo }: { promo: PromoGroup }) {
  const [open, setOpen] = useState(false);
  const endingSoon =
    promo.endDate !== null &&
    (new Date(promo.endDate).getTime() - Date.now()) / 86_400_000 <= 14;

  return (
    <div className="kd-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, color: 'var(--kd-navy)', fontSize: 15 }}>{promo.code}</div>
          <div className="kd-muted" style={{ fontSize: 12 }}>
            {promo.itemCount} item{promo.itemCount === 1 ? '' : 's'}
            {promo.startDate ? ` · ${promo.startDate}` : ''}{promo.endDate ? ` → ${promo.endDate}` : ''}
            {promo.hasCustomerSpecific ? ' · some customer-specific' : ''}
          </div>
        </div>
        {endingSoon && (
          <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '3px 9px', background: '#fff1d9', color: '#a8770a', whiteSpace: 'nowrap' }}>
            Ends {promo.endDate}
          </span>
        )}
        <span className="kd-muted" aria-hidden>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--kd-line)' }}>
          {promo.items.map((i) => <PromoItemRow key={i.partNo} item={i} />)}
          {promo.itemCount > promo.items.length && (
            <div className="kd-muted" style={{ fontSize: 11.5, padding: '8px 0 0' }}>
              Showing {promo.items.length} of {promo.itemCount} items — search inventory for the rest.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PromoItemRow({ item }: { item: PromoItem }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  // Resolve the part number to its inventory record, then open the detail
  // screen (which shows the customer-aware promo price via the quote).
  const openItem = async () => {
    setBusy(true);
    try {
      const hits = await api.inventory.search({ q: item.partNo, limit: 10 });
      const exact = hits.find((h) => h.partNo === item.partNo) ?? hits[0];
      if (!exact) {
        toast(`${item.partNo} not found in inventory`, 'error');
        return;
      }
      void navigate({ to: '/inventory/$id', params: { id: String(exact.spireId) } });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Lookup failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const priceLabel =
    item.amountType === 'price'
      ? money2(item.amount)
      : item.amountType === 'discount'
        ? `${item.amount}% off`
        : 'special';

  return (
    <button
      type="button"
      onClick={() => void openItem()}
      disabled={busy}
      style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, padding: '9px 0', background: 'none', border: 'none', borderBottom: '1px solid var(--kd-line)', cursor: 'pointer', textAlign: 'left', opacity: busy ? 0.6 : 1 }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, color: 'var(--kd-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.description ?? item.partNo}
        </div>
        <div className="kd-muted" style={{ fontSize: 11.5 }}>
          {item.partNo}{item.uom ? ` · ${item.uom}` : ''}
        </div>
      </div>
      <span style={{ fontWeight: 700, color: 'var(--kd-green-dark)', whiteSpace: 'nowrap', fontSize: 14 }}>
        {priceLabel}
      </span>
    </button>
  );
}
